import { getConfigTenant, isCorpTenant, useUserContext, getTrackingEntryPoint } from "@capone/common";
import { AVAILABLE, getExperimentVariant } from "@capone/experiments";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionLink,
  CloseButtonIcon,
  Icon,
  IconName,
  MobileCorpMenuDropdown,
  MobilePopoverCard,
  TRAVEL_CREDIT_AND_OFFER_TITLE,
} from "halifax";
import H from "history";
import React from "react";
import { useHistory } from "react-router-dom";
import {
  B2B_PORTAL_ENDSESSION_PATH,
  PORTAL_SWITCHED,
  PortalSwitchedProperties,
  PortalTypes,
  Tenant,
  VIEWED_CONTACT_MODAL,
} from "redmond";
import { trackEvent } from "../../api/v1/trackEvent";
import {
  NOT_SELF_FUNDED,
  PACKAGES_EXPERIMENT,
  PACKAGES_EXPERIMENT_VARIANTS,
  SELF_FUNDED,
  STAYS_HOMEPAGE,
  getExperimentVariantCustomVariants,
  useExperiments,
} from "../../context/experiments";
import config from "../../utils/config";
import {
  PATH_CARS,
  PATH_CUSTOMER_PROFILE,
  PATH_ACTIVITIES,
  PATH_FLIGHTS,
  PATH_HOTELS,
  PATH_HOTELS_FLIGHTS,
  PATH_PACKAGES,
  PATH_PREMIER_COLLECTION,
  PATH_TRAVEL_WALLET,
  PATH_TRIPS,
} from "../../utils/urlPaths";
import { SupportModalContent } from "../Header/components/SupportModal";
import "./styles.scss";

interface IStyledMainNavLinkProps {
  label: string | React.ReactElement;
  path: string;
}

interface ConnectedMainNavLinkProps extends IStyledMainNavLinkProps {
  history: H.History;
  onClose: () => void;
}
const StyledMainNavLink = ({
  label,
  path,
  history,
  onClose,
}: ConnectedMainNavLinkProps) => (
  <ActionLink
    className="b2b-main-nav-link"
    content={
      typeof label === "string" ? (
        <Typography variant="h3">{label}</Typography>
      ) : (
        label
      )
    }
    onClick={() => {
      history.push(path);
      onClose();
    }}
  />
);

interface IStyledSubNavLinkProps {
  label: string | JSX.Element;
  onClick: () => void;
}

interface ConnectedStyledSubNavLinkProps extends IStyledSubNavLinkProps {
  onClose: () => void;
}

const StyledSubNavLink = ({
  label,
  onClick,
  onClose,
}: ConnectedStyledSubNavLinkProps) => (
  <ActionLink
    className="b2b-sub-nav-link"
    content={<Typography variant="h5">{label}</Typography>}
    onClick={() => {
      onClick();
      onClose();
    }}
  />
);

const MobileHamburgerMenu = ({
  onClose,
  offerCount,
  isTravelWalletOfferExperiment,
  isPremierCollectionEnabled,
  corporatePortalUrl,
  consumerPortalUrl,
  adminPortalUrl,
  tenant,
  isCustomerProfileExperiment,
  showCustomerProfileNewBadge,
  isExperiencesExperiment,
  openSupportModal,
  setOpenSupportModal,
}: {
  onClose: () => void;
  offerCount?: number;
  isTravelWalletOfferExperiment?: boolean;
  isPremierCollectionEnabled?: boolean;
  tenant: Tenant;
  corporatePortalUrl?: string;
  consumerPortalUrl?: string;
  adminPortalUrl?: string;
  isCustomerProfileExperiment?: boolean;
  showCustomerProfileNewBadge?: boolean;
  isExperiencesExperiment?: boolean;
  openSupportModal: boolean;
  setOpenSupportModal: (isOpen: boolean) => void;
}) => {
  const history = useHistory();
  const { logo } = useUserContext(config.TENANT);

  const getLinkLabel = (
    text: string,
    icon?: JSX.Element,
    endIcon?: IconName,
    showNewBadge?: boolean
  ) => (
    <Box className="hamburger-funnel-link-icon-label-wrapper">
      <Box className="funnel-link-icon-and-text">
        {icon}
        <Typography className="label-text">{text}</Typography>
        {showNewBadge && <Box className="nav-item-new-badge">New</Box>}
      </Box>
      {endIcon && <Icon name={endIcon} />}
    </Box>
  );
  const ConnectedSubStyledNavLink = (props: IStyledSubNavLinkProps) => (
    <StyledSubNavLink {...props} onClose={onClose} />
  );
  const ConnectedMainStyledNavLink = (props: IStyledMainNavLinkProps) => (
    <StyledMainNavLink {...props} onClose={onClose} history={history} />
  );
  const handleSupportModalClose = () => setOpenSupportModal(false);

  const trackPortalSwitched = (from: PortalTypes, to: PortalTypes) => {
    trackEvent({
      eventName: PORTAL_SWITCHED,
      properties: {
        current_portal: from,
        portal_selected: to,
      } satisfies PortalSwitchedProperties,
    });
  };

  const expState = useExperiments();
  const isCorporate = isCorpTenant(getConfigTenant());

  const packagesExperimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    PACKAGES_EXPERIMENT,
    PACKAGES_EXPERIMENT_VARIANTS
  );

  const isPackagesExperiment = [SELF_FUNDED, NOT_SELF_FUNDED].includes(
    packagesExperimentVariant
  );

  const staysHomepageEnabled =
    getExperimentVariant(expState.experiments, STAYS_HOMEPAGE) === AVAILABLE;

  const getPackagesDisplayName = () => {
    if (!isCorporate) {
      return "Packages";
    }
    return "Hotels + Flights";
  };

  return (
    <Box className="hamburger-menu">
      <MobilePopoverCard
        className="mobile-contact-support-popup"
        open={openSupportModal}
        onClose={handleSupportModalClose}
        centered
        topRightButton={<CloseButtonIcon onClick={handleSupportModalClose} />}
      >
        <Box className="mobile-contact-support-popup-content">
          <SupportModalContent />
        </Box>
      </MobilePopoverCard>
      <Box className="hamburger-top-section">
        <img className="hamburger-logo" src={logo?.src} alt={logo?.alt} />
        <ActionLink
          className="hamburger-close-button"
          content={<CloseButtonIcon />}
          label="Close"
          onClick={onClose}
        />
      </Box>
      <Box className="hamburger-mid-section">
        <ConnectedMainStyledNavLink
          label={getLinkLabel(
            staysHomepageEnabled ? "Stays" : "Hotels",
            <Icon name={IconName.HotelFunnelIcon} />,
            IconName.RightChevronIcon
          )}
          path={PATH_HOTELS}
        />

        <ConnectedMainStyledNavLink
          label={getLinkLabel(
            "Flights",
            <Icon name={IconName.FlightFunnelIcon} />,
            IconName.RightChevronIcon
          )}
          path={PATH_FLIGHTS}
        />

        <ConnectedMainStyledNavLink
          label={getLinkLabel(
            "Car Rentals",
            <Icon name={IconName.CarFunnelIcon} />,
            IconName.RightChevronIcon
          )}
          path={PATH_CARS}
        />
        {isPackagesExperiment && (
          <ConnectedMainStyledNavLink
            label={getLinkLabel(
              getPackagesDisplayName(),
              <Box display="flex">
                <Icon name={IconName.HotelFunnelIcon} />
                <Typography className="icon-plus-separator">+</Typography>
                <Icon name={IconName.FlightFunnelIcon} />
              </Box>,
              IconName.RightChevronIcon
            )}
            path={
              isCorpTenant(config.TENANT) ? PATH_HOTELS_FLIGHTS : PATH_PACKAGES
            }
          />
        )}
        {isPremierCollectionEnabled && (
          <ConnectedMainStyledNavLink
            label={getLinkLabel(
              "Premium Stays",
              <Icon name={IconName.HotelFunnelIcon} />,
              IconName.RightChevronIcon
            )}
            path={PATH_PREMIER_COLLECTION}
          />
        )}
        {isExperiencesExperiment && (
          <ConnectedMainStyledNavLink
            label={getLinkLabel(
              "Activities",
              <Icon name={IconName.ExperiencesIcon} />,
              IconName.RightChevronIcon
            )}
            path={PATH_ACTIVITIES}
          />
        )}
      </Box>
      {(isCorpTenant(tenant) || corporatePortalUrl) && (
        <MobileCorpMenuDropdown
          tenant={tenant}
          corporatePortalUrl={corporatePortalUrl}
          consumerPortalUrl={consumerPortalUrl}
          adminPortalUrl={adminPortalUrl}
          trackPortalSwitched={trackPortalSwitched}
        />
      )}

      <Box className={clsx("hamburger-bottom-section", tenant)}>
        <Box className="hamburger-sub-links">
          {isTravelWalletOfferExperiment && (
            <ConnectedSubStyledNavLink
              label={
                <Box className="offer-nav-link-container">
                  {getLinkLabel(
                    TRAVEL_CREDIT_AND_OFFER_TITLE,
                    <Icon name={IconName.TravelWalletIcon} />
                  )}
                  {!!offerCount && (
                    <Typography className="offer-count">
                      {offerCount}
                    </Typography>
                  )}
                </Box>
              }
              onClick={() => {
                const entryPoint = window.location.pathname;
                history.push(`${PATH_TRAVEL_WALLET}?entry_point=${getTrackingEntryPoint(entryPoint)}`);
              }}
            />
          )}
          <ConnectedSubStyledNavLink
            label={getLinkLabel(
              "My trips",
              <Icon name={IconName.MyTripIcon} />
            )}
            onClick={() => {
              const entryPoint = window.location.pathname;
              history.push(`${PATH_TRIPS}?entry_point=${getTrackingEntryPoint(entryPoint)}`);
            }}
          />
          <StyledSubNavLink
            onClose={() => {}}
            label={getLinkLabel(
              "Support",
              <Icon name={IconName.QuestionMarkSupportIcon} />
            )}
            onClick={() => {
              trackEvent({
                eventName: VIEWED_CONTACT_MODAL,
                properties: { contact_reason: "general_contact" },
              });
              setOpenSupportModal(true);
            }}
          />
          {isCustomerProfileExperiment && (
            <StyledSubNavLink
              onClose={() => {}}
              label={getLinkLabel(
                "My travel profile",
                <Icon name={IconName.UserOutline} />,
                undefined,
                showCustomerProfileNewBadge
              )}
              onClick={() => {
                history.push(PATH_CUSTOMER_PROFILE);
                onClose?.();
              }}
            />
          )}
        </Box>
        <ActionLink
          className="hamburger-menu-logout"
          content={getLinkLabel("Logout")}
          onClick={() => {
            history.push(B2B_PORTAL_ENDSESSION_PATH);
          }}
        />
      </Box>
    </Box>
  );
};

export default MobileHamburgerMenu;
